// Admin → Executable Skills — the Sandbox UX.
//
// Surfaces the S-EXEC sandbox for operators: the two-level gate state, the
// per-tenant consent toggle (the "switch"), the enforced isolation guarantees,
// which skills carry code, and run history. Nothing here runs code — the
// feature stays gated on the platform kill-switch (flipped on only after the
// security red-team certifies the cage). The toggle records the tenant's
// consent so it takes effect the moment certification lands.

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { COLORS } from "../../theme/colors";
import { skillsApi } from "../../api/client";
import ScriptReviewDrawer from "../skills/ScriptReviewDrawer";
import ArtifactChips from "../skills/ArtifactChips";
import { runOutcomeTone, runMeta } from "../skills/artifactChip";

const REVIEW_BADGE = { approved: { t: "approved", c: COLORS.green || "#10b981" }, stale: { t: "re-review", c: "#d97706" }, rejected: { t: "rejected", c: COLORS.red || "#ef4444" }, pending: { t: "pending", c: "#d97706" }, unreviewed: { t: "needs review", c: "#9090a8" },
};

const BORDER = COLORS.border || "#e2e5eb";
const MUTED = COLORS.textMuted || "#6b7280";
const DIM = COLORS.textDim || "#9090a8";
const GREEN = COLORS.green || "#10b981";
const RED = COLORS.red || "#ef4444";
const ACCENT = COLORS.accent || "#3b82f6";

function unwrap(res) {
  return res && res.data && typeof res.data === "object" ? res.data : res;
}

function Toggle({ checked, disabled, onChange, label }) {
  const on = !!checked;
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled}
      onClick={onChange}
      style={{
        position: "relative", width: 44, height: 24, flex: "none", borderRadius: 999,
        border: `1px solid ${on ? ACCENT : BORDER}`, background: on ? ACCENT : (COLORS.surfaceHover || "#f1f5f9"),
        cursor: disabled ? "not-allowed" : "pointer", padding: 0, opacity: disabled ? 0.55 : 1,
        transition: "background .15s, border-color .15s",
      }}>
      <span style={{
        position: "absolute", top: 2, left: on ? 22 : 2, width: 18, height: 18, borderRadius: "50%",
        background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,.3)", transition: "left .15s",
      }} />
    </button>
  );
}

const card = {
  border: `1px solid ${BORDER}`, borderRadius: 12, background: COLORS.surface,
  padding: "16px 18px", marginBottom: 14,
};
const label = { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: DIM };

export default function ExecutableSkillsTab() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [reviewing, setReviewing] = useState(null); // the script row under review

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [st, sc, rn] = await Promise.all([
        skillsApi.sandboxStatus().then(unwrap).catch(() => null),
        skillsApi.sandboxScripts().then(unwrap).catch(() => ({ skills: [] })),
        skillsApi.sandboxRuns().then(unwrap).catch(() => ({ runs: [], note: "" })),
      ]);
      setStatus(st);
      setScripts((sc && sc.skills) || []);
      setRuns(rn || { runs: [], note: "" });
    } catch {
      setError("Couldn't load sandbox status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleConsent = useCallback(async () => {
    if (busy || !status) return;
    const next = !status.tenant_enabled;
    setBusy(true);
    try {
      const st = unwrap(await skillsApi.sandboxEnable(next));
      setStatus(st);
    } catch {
      setError("Couldn't update the consent preference.");
    } finally {
      setBusy(false);
    }
  }, [busy, status]);

  if (loading) return <div style={{ padding: 20, color: DIM, fontSize: 13 }}>Loading executable-skills status…</div>;
  if (error && !status) return <div style={{ padding: 20, color: RED, fontSize: 13 }}>{error}</div>;

  const s = status || {};
  const effective = !!s.effective_enabled;
  const lim = s.limits || {};

  return (
    <div style={{ maxWidth: 860 }}>
      <button
        type="button"
        onClick={() => navigate("/skills?tab=developer")}
        style={{ background: "none", border: "none", padding: 0, marginBottom: 10, font: "inherit", fontSize: 12.5, fontWeight: 600, cursor: "pointer", color: COLORS.accentText || COLORS.accent }}
      >‹ Back to Skills</button>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, margin: 0, color: COLORS.text }}>Executable Skills — the sandbox</h2>
        <p style={{ margin: "5px 0 0", color: DIM, fontSize: 13, maxWidth: "72ch" }}>
          Some skills carry code (a <code>scripts/</code> tier). When you enable it for your workspace, that code
          runs in a hardened, per-run gVisor sandbox — never with network or credentials, and only after an
          administrator reviews the script. Running author-supplied code is the highest-risk surface on the
          platform, so it is <strong>off by default</strong> and only runs for a workspace that opts in.
        </p>
      </div>

      {/* Effective status + opt-in toggle */}
      <div style={card}>
        <div style={label}>Status</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0 14px" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 7, fontSize: 14, fontWeight: 700,
            padding: "5px 12px", borderRadius: 999,
            color: effective ? GREEN : MUTED,
            background: effective ? `${GREEN}1F` : (COLORS.surfaceHover || "#f1f5f9"),
            border: `1px solid ${effective ? GREEN : BORDER}`,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: effective ? GREEN : DIM }} />
            {effective ? "Enabled — reviewed skills can run (sandboxed)" : "Disabled — no code runs"}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 12, borderTop: `1px solid ${BORDER}` }}>
          <Toggle checked={s.tenant_enabled} disabled={busy} onChange={toggleConsent}
            label="Enable executable skills for this workspace" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 650, color: COLORS.text }}>Enable executable skills for this workspace</div>
            <div style={{ fontSize: 12, color: DIM, marginTop: 2 }}>
              {s.tenant_enabled
                ? "On — this workspace's reviewed skills may run code in the sandbox."
                : "Off — this workspace will not run any skill code until you opt in."}
            </div>
          </div>
        </div>
      </div>

      {/* Isolation guarantees */}
      <div style={card}>
        <div style={label}>Isolation — enforced on every run</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, marginTop: 10 }}>
          <Guarantee k="Runtime" v={`${lim.runtime_class || "gvisor"} (runsc)`} />
          <Guarantee k="Network" v={lim.network || "none"} />
          <Guarantee k="Credentials" v={lim.credentials || "none"} />
          <Guarantee k="Root filesystem" v={lim.rootfs || "read-only"} />
          <Guarantee k="Wall-clock cap" v={`${lim.wall_clock_s ?? "?"}s`} />
          <Guarantee k="CPU / Memory" v={`${lim.cpu ?? "?"} / ${lim.mem_mb ?? "?"} MB`} />
          <Guarantee k="Process cap" v={`${lim.pids ?? "?"} pids`} />
          <Guarantee k="Scratch" v={`${lim.scratch_mb ?? "?"} MB (wiped at run end)`} />
          <Guarantee k="Files out" v={`≤${lim.max_artifact_files ?? 20} · ${lim.max_artifact_total_mb ?? 250} MB · 12h`} />
        </div>
        <div style={{ fontSize: 12, color: DIM, marginTop: 11 }}>
          Runtime, network, credentials, and root filesystem are fixed security
          guarantees — they can't be changed. The resource caps below are adjustable.
        </div>
      </div>

      {/* Adjustable resource caps */}
      <CapsEditor lim={lim} bounds={s.limit_bounds || {}} onSaved={setStatus} />

      {/* Code interpreter */}
      <div style={card}>
        <div style={label}>Code interpreter</div>
        <div style={{ fontSize: 12.5, color: DIM, marginTop: 8 }}>
          Agents can write &amp; run Python on the fly to compute answers and generate downloadable
          files — no author, no review needed (it's your agent, your data). Enable it per agent from the
          Internal Tools list in the builder.
        </div>
        <div style={{ marginTop: 11, display: "flex", gap: 7, flexWrap: "wrap" }}>
          {["xlsx", "pptx", "pdf", "png", "html", "csv", "docx"].map((x) => (
            <span key={x} style={{
              fontSize: 11, fontWeight: 650, color: MUTED, background: COLORS.surfaceHover || "#f1f5f9",
              border: `1px solid ${BORDER}`, borderRadius: 999, padding: "3px 9px",
            }}>.{x}</span>
          ))}
        </div>
        <div style={{ fontSize: 12, color: DIM, marginTop: 11 }}>
          Bring your own data: the agent can pass rows it fetched from a connected database (MCP),
          an OpenAPI/REST integration, or the query toolset into the code as read-only inputs — the
          sandbox has no network, so nothing is fetched from inside it.
        </div>
        <div style={{ fontSize: 12, color: DIM, marginTop: 9 }}>
          Blocked from download: executables &amp; active content (<code>.exe/.sh/.js/</code>macros) —
          served download-only, never rendered here.
        </div>
      </div>

      {/* Skills that carry code */}
      <div style={card}>
        <div style={label}>Skills that carry code ({scripts.length})</div>
        {scripts.length === 0 ? (
          <div style={{ fontSize: 12.5, color: DIM, marginTop: 8 }}>
            No skills carry executable scripts. Imported bundles with a <code>scripts/</code> tier show here —
            stored, never auto-run.
          </div>
        ) : (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
            {scripts.map((sk) => {
              const badge = REVIEW_BADGE[sk.review_state] || REVIEW_BADGE.unreviewed;
              return (
                <div key={sk.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: `1px solid ${BORDER}`, borderRadius: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{sk.name}</span>
                  <span style={{ fontSize: 11, color: DIM }}>v{sk.version} · {sk.status}</span>
                  <span style={{
                    fontSize: 10.5, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase",
                    color: badge.c, background: `${badge.c}1c`, border: `1px solid ${badge.c}55`,
                    borderRadius: 999, padding: "2px 8px",
                  }}>{badge.t}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 11, color: MUTED, fontFamily: "monospace" }}>{(sk.scripts || []).join(", ")}</span>
                  <button type="button" onClick={() => setReviewing(sk)} style={{ border: `1px solid ${BORDER}`, background: COLORS.surfaceHover || "#f1f5f9", color: COLORS.text, borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Review</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Run history */}
      <div style={card}>
        <div style={label}>Run history</div>
        {(runs && runs.runs && runs.runs.length) ? (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
            {runs.runs.slice(0, 10).map((r) => {
              const tone = runOutcomeTone(r.outcome);
              const c = tone === "ok" ? (COLORS.green || "#10b981") : tone === "bad" ? (COLORS.red || "#ef4444") : DIM;
              return (
                <div key={r.id || r.run_id} style={{
                  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                  padding: "10px 12px", border: `1px solid ${BORDER}`, borderRadius: 10,
                }}>
                  <span style={{
                    fontSize: 10.5, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase",
                    color: c, background: `${c}1c`, border: `1px solid ${c}55`,
                    borderRadius: 999, padding: "2px 8px",
                  }}>{r.outcome}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{r.skill_name || "code_interpreter"}</span>
                  <span style={{ fontSize: 11, color: DIM, fontFamily: "monospace" }}>{runMeta(r)}</span>
                  {r.artifacts && r.artifacts.length ? (
                    <span style={{ flexBasis: "100%" }}><ArtifactChips artifacts={r.artifacts} /></span>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: DIM, marginTop: 8 }}>
            {(runs && runs.note) || "Runs appear here once executable skills are enabled for your workspace."}
          </div>
        )}
      </div>

      {reviewing && (
        <ScriptReviewDrawer skill={reviewing} onClose={() => setReviewing(null)} onReviewed={() => { setReviewing(null); load(); }} />
      )}
    </div>
  );
}

function Guarantee({ k, v }) {
  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, padding: "8px 10px", background: COLORS.surfaceHover || "#f8fafc" }}>
      <div style={{ fontSize: 10.5, color: DIM, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 }}>{k}</div>
      <div style={{ fontSize: 12.5, color: COLORS.text, marginTop: 2 }}>{v}</div>
    </div>
  );
}

const _CAPS = [
  { key: "wall_clock_s", label: "Wall-clock (s)" },
  { key: "cpu", label: "CPU (cores)" },
  { key: "mem_mb", label: "Memory (MB)" },
  { key: "scratch_mb", label: "Scratch (MB)" },
];
// Styles as consts (not inline object literals) so v8 doesn't leave the style
// attribute a phantom uncovered continuation line.
const _capInputStyle = { width: "100%", marginTop: 4, padding: "6px 8px", fontSize: 13, border: `1px solid ${BORDER}`, borderRadius: 6, background: COLORS.surface || "#fff", color: COLORS.text, boxSizing: "border-box" };
const _capBtnStyle = (on) => ({ fontSize: 12.5, fontWeight: 650, padding: "7px 14px", borderRadius: 8, border: "none", cursor: on ? "pointer" : "default", color: "#fff", background: on ? ACCENT : (COLORS.surfaceHover || "#e5e7eb"), opacity: on ? 1 : 0.7 });

// Editable RESOURCE caps (the only tunable part — the isolation invariants are
// fixed). Bounded number inputs (min/max/default from the server's limit_bounds);
// Save PUTs them (server clamps) and refreshes status.
function CapsEditor({ lim, bounds, onSaved }) {
  const [draft, setDraft] = useState(() => {
    const o = {}; _CAPS.forEach(({ key }) => { o[key] = Number(lim[key]); }); return o;
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const dirty = _CAPS.some(({ key }) => Number(lim[key]) !== Number(draft[key]));

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      onSaved(unwrap(await skillsApi.sandboxLimits(draft)));
    } catch {
      setErr("Couldn't save the caps.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={card}>
      <div style={label}>Resource caps — adjustable</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginTop: 10 }}>
        {_CAPS.map(({ key, label: kl }) => {
          const b = bounds[key] || {};
          return (
            <label key={key} style={{ display: "block" }}>
              <div style={{ fontSize: 10.5, color: DIM, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 }}>{kl}</div>
              <input type="number" min={b.min} max={b.max} value={draft[key]} style={_capInputStyle}
                onChange={(e) => setDraft({ ...draft, [key]: Number(e.target.value) })} />
              {b.min != null && <div style={{ fontSize: 10.5, color: DIM, marginTop: 2 }}>{b.min}–{b.max} · default {b.default}</div>}
            </label>
          );
        })}
      </div>
      {err && <div style={{ fontSize: 12, color: RED, marginTop: 8 }}>{err}</div>}
      <div style={{ marginTop: 11, display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={save} disabled={!dirty || saving} style={_capBtnStyle(dirty && !saving)}>
          {saving ? "Saving…" : "Save caps"}
        </button>
        <span style={{ fontSize: 12, color: DIM }}>Clamped to the platform bounds on save.</span>
      </div>
    </div>
  );
}
